/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import Files from "./FilemanagerFiles";
import LeftSideTree from "./FileManagerLeftSideTree";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import { CircularProgress } from "@mui/material";
import useFileManagerComponent from "./useFileManagerComponent";
import type { ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import type {
  FileManagerData,
  FileManagerRequestArgs,
  FileManagerRow,
  FileManagerStateUpdate,
  FileManagerSuccessArgs,
} from "./fileSystemServer.schema";

export interface ContextMenuItem {
  id: string;
  name: string;
  icon?: string;
  /**
   * Invoked by `useFileManagerFiles.handleMenuClick` with the SELECTED ROWS, not a mouse event —
   * which is what every handler in `features/files` already declares (the `as ContextMenuItem[]`
   * assertions at the `FileManagerNew` call site exist only because this said `MouseEvent`).
   */
  onClick?: (selectedFiles: FileManagerRow[]) => void;
}

/**
 * The imperative surface this component bolts onto its ref. Everything is optional because the
 * methods are attached in an effect after mount, and the grid half of the handle is replaced with a
 * bare `{}` when the files pane switches to icons view.
 */
export interface FileManagerHandle {
  dataView?: { items?: FileManagerRow[]; getAllSelectedItems: () => FileManagerRow[] };
  slickGrid?: {
    getSelectedRows: () => number[];
    setSelectedRows: (rows: number[]) => void;
    getDataItem: (row: number) => FileManagerRow;
  };
  refreshFileManager?: () => void;
  refreshFiles?: () => void | Promise<void>;
  filesViewMode?: string;
  getSelectedFilesInFileManager?: () => FileManagerRow[];
  showLoading?: () => void;
  hideLoading?: () => void;
  navigateToFolder?: (folderId: string) => void;
  setSelectedRows?: (rows: (string | number)[]) => void;
  /** Selection carried across a `refreshFiles()` round-trip. */
  _pendingSelectedRows?: number[] | null;
}

/** A file-manager toolbar entry: the grid's toolbar item plus the two keys this layer reads. */
type FileManagerToolbarItem = ToolbarItem & { name?: string; show?: unknown };

/** The extra props project ("Folders") mode hands the files grid; see `renderTreeAndGrid`. */
interface FilesExtraProps {
  toolbarContainer?: HTMLElement | null;
  commonToolbar?: ToolbarItem[];
  commonToolbarClickHandler?: (id: string) => void;
  disableOuterBorder?: boolean;
}

interface FileManagerComponentProps {
  /**
   * Active external-link count per row id (CP-283, FR-7), forwarded to the icons view so it can
   * badge the thumbnail. Sparse; omitted by hosts that do not surface external links.
   */
  externalLinkCounts?: Record<string, number>;
  fileManagerData: FileManagerData;
  allowDragAndDropUpload: boolean;
  onFileClick(clickedRow: FileManagerRow): void;
  onFileSelect(files: FileManagerRow[]): void;
  setCurrentFolder(folder: FileManagerStateUpdate<FileManagerRow>): void;
  toolbarItems: FileManagerToolbarItem[];
  /** `e` is `undefined` on the grid's own toolbar path, so it is not narrowed to a mouse event. */
  toolbarClick(e: unknown, id: string): void;
  contextMenuSettings?: {
    file: ContextMenuItem[];
    folder: ContextMenuItem[];
    layout: ContextMenuItem[];
    visible: boolean;
  };
  /**
   * Returns the ids of the context-menu entries to disable.
   * KNOWN BUG, preserved verbatim: this component's own destructuring default is `() => {}`, which
   * returns nothing — the files grid then calls `.includes()` on it. Unreachable today because the
   * prop is required, so `void` is admitted here rather than changing the default (which is emitted
   * code); the forward to `<Files>` re-asserts the real `string[]` contract.
   */
  menuOpen(selectedFiles?: FileManagerRow[]): string[] | void;
  /** Built against the caller's own row type; this layer only forwards them to the files grid. */
  fileColumns: object[];
  defaultPinnedColumns?: string[];
  setFileManagerData(update: FileManagerStateUpdate<FileManagerData>): void;
  beforeSending(args: FileManagerRequestArgs): void;
  onSuccess(args: FileManagerSuccessArgs): void;
  onFileLoad(files: FileManagerRow[]): void;
  showFolderTree: boolean;
  path: string;
  allowViewFileOptions: boolean;
  disableDragAndDropUpload: boolean;
  preSearchText?: string;
  // Project-level ("Folders") mode: render every Space of the project as a top-level tree root.
  projectMode?: boolean;
  projectId?: string;
  focusSpaceId?: string | undefined;
}

const FileManagerComponent = forwardRef(
  (
    {
      fileManagerData = {
        cwd: {},
        files: [],
      },
      externalLinkCounts,
      allowDragAndDropUpload = false,
      onFileClick = () => {},
      onFileSelect = () => {},
      setCurrentFolder = () => {},
      toolbarItems = [],
      toolbarClick,
      contextMenuSettings = {
        file: [],
        folder: [],
        layout: [],
        visible: true,
      },
      menuOpen = () => {},
      fileColumns = [],
      defaultPinnedColumns,
      setFileManagerData,
      beforeSending = () => {},
      onSuccess = () => {},
      onFileLoad = () => {},
      showFolderTree = true,
      path,
      disableDragAndDropUpload,
      preSearchText,
      projectMode = false,
      projectId,
      focusSpaceId,
    }: FileManagerComponentProps,
    ref: React.Ref<FileManagerHandle>,
  ) => {
    const tempRef = useRef<FileManagerHandle>({});
    const filesRef = (ref || tempRef) as React.RefObject<FileManagerHandle | null>;
    const treeViewRef = useRef(null);
    // Project ("Folders") mode: the shared, full-width slot the grid portals its toolbar into (above the
    // tree + grid). Held in state so the grid re-renders and portals once the slot element is mounted.
    const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);

    const {
      // State
      selectedFolder,
      setSelectedFolder,
      filesViewMode,
      tempFolder,
      refreshGrid,
      selectedFilesInIconsMode,
      setSelectedFilesInIconsMode,
      isViewRendered,
      setIsViewRendered,
      isLoaded,

      // Refs
      splitterSizeRef,

      // Handlers
      refreshFileManager,
      refreshFiles,
      navigateToFolder,
      handleSearchFiles,
      getSelectedFilesInFileManager,
      showLoading,
      hideLoading,
    } = useFileManagerComponent({
      fileManagerData,
      setFileManagerData,
      setCurrentFolder,
      beforeSending,
      onSuccess,
      showFolderTree,
      path,
      projectMode,
      projectId,
      focusSpaceId,
    });
    // Expose methods via ref
    useEffect(() => {
      if (filesRef.current) {
        filesRef.current.refreshFileManager = refreshFileManager;
        filesRef.current.refreshFiles = async () => {
          const selectedRows = filesRef.current?.slickGrid?.getSelectedRows() || [];
          // KNOWN BUG, preserved verbatim: the line above guards `filesRef.current` with `?.` but
          // this one dereferences it unguarded, so a ref detached between the two throws. Erased
          // assertion; adding a guard would change behaviour.
          filesRef.current!._pendingSelectedRows = selectedRows;
          await refreshFiles();
        };
        filesRef.current.filesViewMode = filesViewMode;
        filesRef.current.getSelectedFilesInFileManager = () => getSelectedFilesInFileManager(filesRef);
        filesRef.current.showLoading = showLoading;
        filesRef.current.hideLoading = hideLoading;
        filesRef.current.navigateToFolder = navigateToFolder;
      }
    }, [filesRef.current, isViewRendered, beforeSending, onSuccess, filesViewMode, selectedFilesInIconsMode]);

    // The resizable tree + grid split. `filesExtraProps` lets project mode hand the grid the shared toolbar
    // slot + adapted toolbar items; single-space/popup pass nothing (grid toolbar stays hidden).
    const renderTreeAndGrid = (filesExtraProps: FilesExtraProps = {}) => (
      <SplitLayout
        initialSizes={splitterSizeRef.current}
        gutterWidth={"1px"}
        gutterStyle={{ position: "absolute", zIndex: 50 }}
        minSizes={[20, 45]}
        style={{ display: "flex", height: "100%" }}
        showProperties={true}
      >
        {[
          showFolderTree && (
            <div
              key="folderTree"
              className="flex h-full border border-s-0 border-t-0 border-b-0 border-e-stroke_primary_light overflow-auto"
            >
              <LeftSideTree
                ref={treeViewRef}
                onFileLoad={onFileLoad}
                fileManagerData={fileManagerData}
                totalFolderCount={fileManagerData.totalDirectoryCount}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
              />
            </div>
          ),
          <div key="filesGrid" className="h-full flex">
            <Files
              ref={filesRef}
              preSearchText={preSearchText}
              setIsViewRendered={setIsViewRendered}
              isViewRendered={isViewRendered}
              filesViewMode={filesViewMode}
              setSelectedFilesInIconsMode={setSelectedFilesInIconsMode}
              selectedFilesInIconsMode={selectedFilesInIconsMode}
              allowDragAndDropUpload={allowDragAndDropUpload}
              onFileLoad={onFileLoad}
              tempFolder={tempFolder}
              beforeSending={beforeSending}
              handleSearchFiles={handleSearchFiles}
              fileColumns={fileColumns}
              defaultPinnedColumns={defaultPinnedColumns}
              menuOpen={menuOpen as (selectedFiles?: FileManagerRow[]) => string[]}
              contextMenuSettings={contextMenuSettings}
              fileManagerData={fileManagerData}
              externalLinkCounts={externalLinkCounts}
              onFileSelect={onFileSelect}
              onFileClick={onFileClick}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              disableDragAndDropUpload={disableDragAndDropUpload}
              {...filesExtraProps}
            />
          </div>,
        ].filter(Boolean)}
      </SplitLayout>
    );

    const loadingOverlay = refreshGrid && (
      <div className="absolute h-full w-full flex justify-center items-center z-20 rounded-md">
        <CircularProgress />
      </div>
    );

    const getContent = () => {
      // Adapt the file-manager toolbar items into the grid ToolBar's item shape (text->name, iconName->
      // prefixIcon, align:"right"->rightAllign; wrap JSX templates as functions). They render inside the
      // grid's own fully-wired toolbar, alongside its filter/sort/group/column-chooser controls.
      const commonToolbarItems = (toolbarItems || []).filter((item: FileManagerToolbarItem) => item.show);
      return (
        // Merged card: the grid's toolbar (portaled into the slot) spans the full width above the tree +
        // grid; all three share one rounded border.
        <div className="h-full flex flex-col w-full relative border border-stroke_primary_light rounded-lg overflow-hidden">
          {loadingOverlay}
          {/* Full-width slot the Files grid portals its toolbar into. */}
          <div ref={setToolbarSlot} className="w-full" />
          {renderTreeAndGrid({
            toolbarContainer: toolbarSlot,
            commonToolbar: commonToolbarItems,
            commonToolbarClickHandler: (id: string) => toolbarClick(undefined, id),
            // Grid lives inside the merged card, which owns the border — drop the grid's own outer border.
            disableOuterBorder: true,
          })}
        </div>
      );
    };

    return (
      <div className="w-full h-full items-center justify-center overflow-auto flex flex-col">
        {isLoaded ? getContent() : <CircularProgress />}
      </div>
    );
  },
);

FileManagerComponent.displayName = "FileManagerComponent";
export default FileManagerComponent;
